/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */

import { cn } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";
import { SelectInput, SelectItem } from "../form/SelectInput";

type ChildProps = HTMLDivElement & {
  value?: string;
  ["data-label"]?: string;
  disabled?: boolean;
};

const TabsContext = React.createContext<{
  value?: string;
  onValueChange?: (value: string) => void;
}>({ value: "" });
const useTabs = () => {
  return React.useContext(TabsContext);
};
const tabsVariants = cva("", {
  variants: {
    variant: {
      settingsTabs: "flex h-full flex-col",
      default: ""
    }
  }
});

/** Holds the tabs' state (`value` / `defaultValue`); `variant="settingsTabs"` fills the height. */
const Tabs = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> &
    VariantProps<typeof tabsVariants>
>(({ className, variant = "default", ...props }, ref) => (
  <TabsContext.Provider
    value={{ value: props.value, onValueChange: props.onValueChange }}
  >
    <TabsPrimitive.Root
      ref={ref}
      className={cn(tabsVariants({ variant }), className)}
      {...props}
    />
  </TabsContext.Provider>
));
Tabs.displayName = TabsPrimitive.Root.displayName;

const tabsListVariants = cva(
  "flex flex-row flex-wrap items-center justify-start gap-x-4 gap-y-2 rounded-md text-content active:ring-0 focus:border-none focus:after:border-none focus:after:ring-0 focus:outline-none focus:ring-0 focus:outline-none selected:border-none selected:ring-0",
  {
    variants: {
      variant: {
        settingsTabs: "mt-settings-post-title mb-4",
        default: ""
      }
    }
  }
);
export type TabsListProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> &
  VariantProps<typeof tabsListVariants> & {
    /** On phones, a select of the tabs replaces the row; needs a controlled `Tabs`. */
    showTabsAsDropdownInMobile?: boolean;
    value?: string;
    onValueChange?: (value: string) => void;
    /** Label of a tab in the phone select; the trigger's text by default. */
    getOptionLabel?: (trigger: React.ReactElement) => string;
  };

const defaultGetOptionLabel = (
  child: React.ReactElement<ChildProps>
): string => {
  if (child.props?.["data-label"]) return String(child.props["data-label"]);
  const c = child.props?.children;
  if (typeof c === "string" || typeof c === "number") return String(c);
  if (Array.isArray(c)) {
    const flat = c
      .map((n) => (typeof n === "string" || typeof n === "number" ? n : ""))
      .join(" ")
      .trim();
    if (flat) return flat;
  }
  return child.props?.value ?? "";
};

const DropdownTabsList: React.FC<TabsListProps> = ({
  children,
  getOptionLabel = defaultGetOptionLabel,
  className
}) => {
  const { value, onValueChange } = useTabs();
  const options = React.Children.toArray(children)
    .filter(Boolean)
    .map((child) => {
      if (!React.isValidElement(child)) return null;
      // Radix's Trigger has no displayName, so the name check alone matches anything
      // unnamed and misses a TabsTrigger that tooling has named: check the components first
      const isTrigger =
        child.type === TabsTrigger ||
        (child.type as any) === TabsPrimitive.Trigger ||
        (child.type as any)?.displayName === TabsPrimitive.Trigger.displayName;

      if (!isTrigger) return null;
      const v = (child.props as ChildProps)?.value as string | undefined;
      if (!v) return null;

      return {
        value: v,
        label: getOptionLabel(child as React.ReactElement<ChildProps>),
        disabled: Boolean((child.props as ChildProps)?.disabled)
      };
    })
    .filter(Boolean) as {
    value: string;
    label: string;
    disabled: boolean;
  }[];

  return (
    // The select shows the current tab; the group says what choosing does
    <div
      role="group"
      aria-label="Tabs"
      className={cn("mb-4 w-full", className)}
    >
      <SelectInput
        value={value ?? options[0]?.value ?? ""}
        onChangeValue={onValueChange}
        variant={"unstyled"}
        wrapperClassName={"w-full"}
      >
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectInput>
    </div>
  );
};

/** The row of tabs; name it with `aria-label`. */
const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  TabsListProps
>(
  (
    { className, variant = "default", showTabsAsDropdownInMobile, ...props },
    ref
  ) => {
    const { isBreakpoint } = useBreakpoint();
    const isMobile = !isBreakpoint("md");

    if (showTabsAsDropdownInMobile && isMobile) {
      return <DropdownTabsList className={className} {...props} />;
    }

    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      />
    );
  }
);
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  cn(
    "relative inline-flex items-center justify-center whitespace-nowrap",
    "rounded-sm py-1.5 text-[14px] ring-offset-background",
    // Keyboard focus must show: the active tab alone does not say where focus is
    "transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:pointer-events-none disabled:opacity-50",
    "cursor-pointer hover:text-foreground font-inter"
  ),
  {
    variants: {
      variant: {
        default:
          "data-[state=active]:bg-background data-[state=active]:text-neutral-1000 data-[state=active]:font-[500] px-2 first:pl-0",
        minimalistic: cn(
          "data-[state=active]:text-neutral-1000 data-[state=active]:font-[500] data-[state=active]:after:absolute",
          "data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:h-[2px] data-[state=active]:after:bg-neutral-1000",
          "data-[state=active]:after:rounded-full text-sm"
        )
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

/** A tab; `variant="minimalistic"` underlines the active one. */
const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants> & {
      /** Marks the tab as having unsaved changes (renders a red dot after the label). */
      hasUnsavedChanges?: boolean;
      /** Marks the tab as holding a validation error (colors the label red). */
      hasError?: boolean;
    }
>(
  (
    { className, variant = "default", hasUnsavedChanges, hasError, ...props },
    ref
  ) => {
    const { children, ...rest } = props;
    return (
      <TabsPrimitive.Trigger
        ref={ref}
        aria-invalid={hasError || undefined}
        className={cn(
          tabsTriggerVariants({ variant }),
          "group/root",
          hasError &&
            "text-red-600 hover:text-red-700 data-[state=active]:text-red-700",
          className
        )}
        {...rest}
      >
        {hasUnsavedChanges ? (
          <span className="inline-flex items-center gap-1.5">
            {children}
            <span
              role="img"
              aria-label="Has unsaved changes"
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500"
            />
          </span>
        ) : (
          children
        )}
      </TabsPrimitive.Trigger>
    );
  }
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const tabsContentVariants = cva(
  cn(
    "ring-offset-background focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
    "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
  ),
  {
    variants: {
      variant: {
        settingsTabsContent: "flex-1 overflow-hidden",
        default: ""
      }
    }
  }
);

/** The panel of the tab with the same `value`. */
const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> &
    VariantProps<typeof tabsContentVariants>
>(({ className, variant = "default", ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(tabsContentVariants({ variant }), className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
